import { attach, createEffect, createStore } from 'effector';
import { toaster } from 'evergreen-ui';
import { LocalStorageKeys } from 'src/common/local-stoage-keys';
import { request } from 'src/common/request';
import { Interest } from 'src/domain/interest';
import { User } from 'src/domain/user';
import { history } from 'src/history';
import { routes } from 'src/routes';

export type CreateUserPayload = {
	first_name: string;
	last_name: string;
	email: string;
	password: string;
}
export type UpdateUserPayload = {
	details: Partial<CreateUserPayload>;
	id: string;
};

export const createUserFx = createEffect<CreateUserPayload, void>();
export const fetchUserFx = createEffect<void, User>();
export const updateUserFx = createEffect<UpdateUserPayload, User>();
export const verifyUserFx = createEffect<{ code: string }, User>();
export const createResetCodeFx = createEffect<{ email: string }, { status: 'ok' }>();
export const resetPasswordFx = createEffect<{ code: string; password: string }, { status: 'ok' }>();

export const user$ = createStore<User | null>(null)
	.on(fetchUserFx.doneData, (_, data) => data)
	.on(updateUserFx.doneData, (_, data) => data);
export const isUserActive$ = user$.map(user => user && [
		user.interests?.length ? user.interests.length > 5 : null,
		user.details.first_name,
		user.details.last_name,
		user.details.sex,
		user.details.dob,
		user.details.phone,
		user.details.state,
		user.details.postal_code,
		user.details.address,
		user.details.city,
		user.details.country,
	]
		.map(item => Boolean(item))
		.every((item) => item),
);

createResetCodeFx.use((payload) => {
	return request.post('/users/restore', payload);
});
resetPasswordFx.use((payload) => {
	return request.patch('/users/restore', payload);
});
createUserFx.use(async (payload) => {
	return request.post('/users', payload);
});
fetchUserFx.use(async () => {
	const { data } = await request.get('/users/me');
	return data;
});
updateUserFx.use(async (payload) => {
	const { data } = await request.patch(`/users/${payload.id}`, payload.details);
	return data;
});
verifyUserFx.use(async (payload) => {
	const { data } = await request.post(`/users/${payload.code}/verify`);
	return data;
});

export const updateUserInterestsFx = attach({
	// @ts-ignore
	source: { user: user$ },
	mapParams: (interests: string[], source) => ({ id: source.user?.id, interests }),
	effect: createEffect<{ id: string, interests: Interest[] }, Interest[]>({
		handler: async ({ id, interests }) => {
			const { data } = await request.patch(`/users/${id}/interests`, { interests });

			return data;
		},
	}),
});
export const updateUserAvatarFx = attach({
	source: { user: user$ },
	mapParams: (payload: { file: File }, source) => ({
		id: source.user?.id,
		file: payload.file,
	}),
	effect: createEffect<{ id?: string, file: File }, User>({
		handler: async (payload) => {
			const data = new FormData();
			data.append('file', payload.file);
			const response = await request.put(`/users/${payload.id}/avatar`, data);
			return response.data;
		},
	}),
});
user$
	.on(updateUserAvatarFx.doneData, (user, data) => (user && {
		...user,
		details: {
			...user.details,
			avatar: data.details.avatar,
		},
	}))
	.on(verifyUserFx.doneData, (user) => (user && {
		...user,
		active: true,
	}));

fetchUserFx.done.watch(() => {
	if (window.location.pathname.includes('auth')) {
		history.push(routes.root.home.path);
	}
});
fetchUserFx.fail.watch(() => {
	localStorage.removeItem(LocalStorageKeys.auth);
	history.push(routes.root.auth.login.path);
});
verifyUserFx.done.watch(() => toaster.success('Your account has been successfully verified'));
verifyUserFx.fail.watch(() => toaster.danger('Verification failed, please check your verification link'));
createResetCodeFx.done.watch(() => toaster.success('If we found an account associated with that username, we\'ve sent password reset instructions to the primary email address on the account'));
createResetCodeFx.fail.watch(() => toaster.danger('Internal server error'));
resetPasswordFx.done.watch(() => history.push(routes.root.auth.login.path));
resetPasswordFx.fail.watch(() => toaster.danger('Invalid reset code'));
